/**
 * Aboutのサービスで、札の写真と節の背景を枠の中で上下に流す
 *
 * @description 枠が画面を通り抜けるあいだ、中身が枠より遅れて下りていく。
 *              振れ幅は中身が枠の外へはみ出している量をDOMから読んで決める。SCSSとJSの
 *              両方に同じ数値を書くと、片方だけ直したときに枠の縁が見える。
 *
 *              位置は offsetTop を辿って測る。札は page-about_service.ts が scale と
 *              translate で動かしているので、getBoundingClientRect で測ると
 *              その変形まで進み具合に混ざる。
 */

/** 監視を止める手続き。非同期遷移で節ごと作り直すときに使う */
let detach: (() => void) | null = null;

/**
 * -1〜1に丸める
 *
 * @param v 元の値
 * @returns -1〜1の値
 */
const clamp1 = (v: number): number => (v < -1 ? -1 : v > 1 ? 1 : v);

/**
 * 変形を抜きにした、ページ上端からの位置を返す
 *
 * @description offsetTop は祖先の transform に影響されない。札が開く途中でも測り値が揺れない
 * @param el 測る要素
 * @returns ページ上端からの位置(px)
 */
const docTopOf = (el: HTMLElement): number => {
    let y = 0;
    let node: HTMLElement | null = el;

    while (node) {
        y += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
    }

    return y;
};

/**
 * 写真の流れを止める
 *
 * @returns {void}
 */
const destroyServiceFig = (): void => {
    detach?.();
    detach = null;
};

/**
 * 写真の流れを始める
 *
 * @returns {void}
 */
const initServiceFig = (): void => {
    destroyServiceFig();

    const list = document.querySelector<HTMLElement>('.service__list');

    if (!list) return;

    // 視差は装飾なので、モーションを抑える設定では動かさない
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // 札の写真は「枠＝picture・動かすのはimg」。節の背景は「枠＝bg・動かすのはpicture」。
    // どちらも枠の中心と、動かす層のはみ出し量だけで決まるので同じ列で扱う
    const boxes = [
        ...list.querySelectorAll<HTMLElement>('.service__listItemInnerHeadFig picture'),
    ].map((box) => ({ box, mover: box.querySelector<HTMLElement>('img') }));

    const bg = list.closest<HTMLElement>('.service')?.querySelector<HTMLElement>('.service__bg');

    if (bg) boxes.push({ box: bg, mover: bg.querySelector<HTMLElement>('picture') });

    const figs = boxes
        .map(({ box, mover }) => ({ box, mover, center: 0, over: 0 }))
        .filter((f): f is { box: HTMLElement; mover: HTMLElement; center: number; over: number } => f.mover !== null);

    if (figs.length === 0) return;

    /**
     * 測り直す
     *
     * @returns {void}
     */
    const measure = (): void => {
        figs.forEach((fig) => {
            fig.center = docTopOf(fig.box) + fig.box.offsetHeight / 2;
            // はみ出しは上下で同じ。SCSSが負の top を置いているのでそのまま量になる
            fig.over = Math.max(0, -fig.mover.offsetTop);
        });
    };

    /**
     * いまのスクロール位置に合わせて写真を置き直す
     *
     * @returns {void}
     */
    const update = (): void => {
        const h = Math.max(1, window.innerHeight);

        figs.forEach((fig) => {
            // 枠の中心が画面の下端で-1、上端で+1。下へスクロールするほど写真は下へ残る
            const p = clamp1(1 - ((fig.center - window.scrollY) / h) * 2);

            fig.mover.style.transform = `translate3d(0, ${(p * fig.over).toFixed(1)}px, 0)`;
        });
    };

    let raf = 0;

    const tick = (): void => {
        raf = requestAnimationFrame(tick);
        update();
    };

    const start = (): void => {
        if (raf) return;

        raf = requestAnimationFrame(tick);
    };

    const stop = (): void => {
        cancelAnimationFrame(raf);
        raf = 0;
    };

    const onResize = (): void => {
        measure();
        update();
    };

    measure();
    update();

    // 節が画面に無いあいだは毎フレームの書き換えを止める。札は絶対配置で並びの箱は
    // 高さを持たないので、高さを持つ節そのものを見る
    const io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) start();
        else stop();
    }, { threshold: 0 });

    io.observe(list.closest<HTMLElement>('.service') ?? list);
    window.addEventListener('resize', onResize, { passive: true });

    detach = () => {
        stop();
        io.disconnect();
        window.removeEventListener('resize', onResize);
        figs.forEach((fig) => fig.mover.style.removeProperty('transform'));
    };
};

export { initServiceFig, destroyServiceFig };

document.addEventListener('DOMContentLoaded', initServiceFig);
